import { useForm, Controller } from 'react-hook-form';
import { Button } from '@artstorefronts/ui';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { UPDATE_PROFILE } from '~/graphql/mutations';
import { useAuth } from '~/context/auth';
import { useAnalytics } from '~/context/analytics';
import { Loader2 } from 'lucide-react';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useEffect, useState } from 'react';
import { SmsConsentCheckboxes } from './SmsConsentCheckboxes';

/** Pinned TCPA consent-copy version sent with updateProfile; must stay ≤ 32 chars (DB VARCHAR). */
const SMS_CONSENT_COPY_VERSION = 'v1';

const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine((value) => {
      if (!value) return false;
      try {
        return isValidPhoneNumber(value);
      } catch {
        return false;
      }
    }, 'Phone number must be a valid international number (e.g., +1 555 123 4567)'),
  smsConsent: z.boolean().refine((v) => v === true, 'SMS consent is required'),
  termsAccepted: z.boolean().refine((v) => v === true, 'Terms acceptance is required'),
});

type FormData = z.infer<typeof phoneSchema>;

export function DailySMSTipsSignup({
  onComplete,
  title,
  description,
}: {
  onComplete?: () => void;
  title?: string;
  description?: string;
}) {
  const { t } = useTranslation('common');
  const { refetchProfile, profile } = useAuth();
  const displayTitle = title || t('sms-tips.title');
  const displayDescription = description || t('sms-tips.description');
  const { amplitude } = useAnalytics();
  const [updateProfile] = useMutation(UPDATE_PROFILE);

  // Check if user already has a phone number
  const existingPhoneNumber = profile?.profile?.phoneNumber;
  const hasPhoneNumber = Boolean(existingPhoneNumber);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phoneNumber: '',
      smsConsent: false,
      termsAccepted: false,
    },
  });

  const phoneNumber = watch('phoneNumber');
  const smsConsent = watch('smsConsent');
  const termsAccepted = watch('termsAccepted');
  const [hasPhoneDigits, setHasPhoneDigits] = useState(false);

  let phoneValid = false;
  if (phoneNumber?.trim()) {
    try {
      phoneValid = isValidPhoneNumber(phoneNumber);
    } catch {
      phoneValid = false;
    }
  }

  const canSubmit = phoneValid && smsConsent && termsAccepted && !isSubmitting;
  // Only surface the live hint once the user has typed real digits — PhoneInput auto-fills the
  // bare dial code (e.g. "+1 ") on mount and on country switch, which would otherwise trip this.
  const showPhoneError = Boolean(errors.phoneNumber) || (hasPhoneDigits && Boolean(phoneNumber?.trim()) && !phoneValid);

  useEffect(() => {
    // Only track phone capture attempt if user doesn't already have a phone number
    if (hasPhoneNumber) return;

    async function track() {
      try {
        await updateProfile({
          variables: {
            input: {
              lastPhoneCaptureAttempt: new Date().toISOString(),
            },
          },
        });
      } catch (error) {
        console.error('Error tracking phone capture attempt:', error);
      }
    }

    track();
  }, [updateProfile, hasPhoneNumber]);

  const onSubmit = async (data: FormData) => {
    try {
      // Parse and format to E.164
      const parsedNumber = parsePhoneNumberFromString(data.phoneNumber);
      const e164Number = parsedNumber?.format('E.164');

      if (!e164Number) {
        toast.error(t('sms-tips.invalid-format'), {
          position: 'top-right',
        });
        return;
      }

      await updateProfile({
        variables: {
          input: {
            phoneNumber: e164Number,
            smsConsent: true,
            termsAccepted: true,
            smsConsentCopyVersion: SMS_CONSENT_COPY_VERSION,
          },
        },
      });

      await refetchProfile();

      amplitude.track('daily_sms_capture_agent');

      toast.success(t('sms-tips.phone-saved'), {
        position: 'top-right',
      });

      onComplete?.();
    } catch (error) {
      toast.error(t('sms-tips.save-failed'), {
        position: 'top-right',
      });
      console.error('Error saving phone number:', error);
    }
  };

  if (hasPhoneNumber) {
    return (
      <div className="max-w-sm p-6 my-8 bg-white rounded-lg border border-gray-200">
        <h3 className="text-xl font-medium text-gray-900 mb-2">{t('sms-tips.tips-active-title')}</h3>
        <p className="text-gray-600 text-sm mt-4">{t('sms-tips.tips-active-description')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-sm p-6 my-8 bg-white rounded-lg border border-gray-200">
      <h3 className="text-xl font-medium text-gray-900 mb-2">{displayTitle}</h3>
      <p className="text-gray-600 text-sm mb-4">{displayDescription}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="flex">
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { onChange, value } }) => (
              <PhoneInput
                defaultCountry="us"
                value={value || ''}
                onChange={(phone, meta) => {
                  onChange(phone);
                  setHasPhoneDigits(phone.replace(/\D/g, '').length > meta.country.dialCode.length);
                }}
                disabled={isSubmitting}
                className="flex-1 h-9"
              />
            )}
          />
          <Button type="submit" disabled={!canSubmit} className="h-9 px-4 whitespace-nowrap">
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('sms-tips.send-tips')}
          </Button>
        </div>
        {showPhoneError && (
          <p className="text-red-600 text-sm">{errors.phoneNumber?.message || t('sms-tips.invalid-format')}</p>
        )}

        <SmsConsentCheckboxes
          idPrefix="sms-consent"
          smsConsent={smsConsent}
          onSmsConsentChange={(checked) => setValue('smsConsent', checked, { shouldValidate: true })}
          smsConsentError={errors.smsConsent?.message}
          termsAccepted={termsAccepted}
          onTermsAcceptedChange={(checked) => setValue('termsAccepted', checked, { shouldValidate: true })}
          termsAcceptedError={errors.termsAccepted?.message}
          disabled={isSubmitting}
        />
      </form>

      <p className="text-xs text-gray-500 mt-3 text-left">{t('sms-tips.disclaimer')}</p>
    </div>
  );
}
